// Lote E: aviso al IESS y medidas correctivas de un caso (incidents e incident_actions, M46). Cada cambio vuelve a
// calcular el Checklist (syncIncidentCompliance): registrar o borrar el aviso y crear, editar, cerrar, reabrir o borrar
// una medida. Los archivos van al bucket privado incident-files (M46): <empresa>/<incidente>/aviso/… y
// <empresa>/<incidente>/medidas/<medida>/…; se ven con una URL firmada de 120 s (lib/archivos.ts).
// La base repite las reglas (triggers de M46): una medida no se cierra sin evidencia y reabrir pide motivo.

import { supabase } from '../../supabaseClient';
import { syncIncidentCompliance } from '../incident-compliance-sync';
import { SEGUNDOS_URL_FIRMADA } from '../archivos';
import { nombreSeguroStorage } from '../nombre-storage';
import { ESTADO_CERRADA } from './inc-estado';
import { esFechaIso } from './inc-plazos';

export const BUCKET_INCIDENTES = 'incident-files' as const;
export const TIPOS_ARCHIVO = ['application/pdf', 'image/png', 'image/jpeg'];
export const MAX_MB = 10;
export const ACEPTA_ARCHIVO = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';

export const MENSAJE_CIERRE_SIN_EVIDENCIA = 'Una medida correctiva no se puede cerrar sin evidencia: adjunta una foto o un archivo.';
export const MIN_MOTIVO_REAPERTURA = 5;
export const MENSAJE_BORRAR_CERRADA = 'Una medida cerrada no se borra: primero reábrela con un motivo.';

export type NivelControl = 'eliminacion' | 'sustitucion' | 'ingenieria' | 'administrativo' | 'epp';
export const NIVELES_CONTROL: { valor: NivelControl; etiqueta: string }[] = [
  { valor: 'eliminacion', etiqueta: 'Eliminación' },
  { valor: 'sustitucion', etiqueta: 'Sustitución' },
  { valor: 'ingenieria', etiqueta: 'Controles de ingeniería' },
  { valor: 'administrativo', etiqueta: 'Controles administrativos' },
  { valor: 'epp', etiqueta: 'Equipo de protección personal' },
];
export const etiquetaNivel = (n: string | null | undefined) => NIVELES_CONTROL.find(x => x.valor === n)?.etiqueta ?? 'Sin nivel';

export const ORIGENES_PRESUNCION: { valor: string; etiqueta: string }[] = [
  { valor: 'vigilancia_salud', etiqueta: 'Vigilancia de la salud' },
  { valor: 'incidente', etiqueta: 'Un incidente o accidente' },
  { valor: 'otro', etiqueta: 'Otro' },
];

export interface FilaMedida {
  id: string;
  incident_id: string;
  company_id: string;
  description: string;
  control_level: string | null;
  responsible_id: string | null;
  responsible_name: string | null;
  due_date: string | null;
  status: string | null;
  follow_up: string | null;
  evidence_path: string | null;
  evidence_name: string | null;
  evidence_mime: string | null;
  closed_at: string | null;
  completed_at: string | null;
  reopened_at: string | null;
  reopen_reason: string | null;
  created_at: string | null;
}
export const COLUMNAS_MEDIDA = 'id, incident_id, company_id, description, control_level, responsible_id, responsible_name, due_date, status, '
  + 'follow_up, evidence_path, evidence_name, evidence_mime, closed_at, completed_at, reopened_at, reopen_reason, created_at';

export interface MedidaEditable {
  descripcion: string;
  nivel: NivelControl | '';
  responsableId: string;
  responsableNombre: string;
  fecha: string;
  seguimiento: string;
}

export const medidaVacia = (): MedidaEditable => ({ descripcion: '', nivel: '', responsableId: '', responsableNombre: '', fecha: '', seguimiento: '' });
export const editableDesdeFila = (f: FilaMedida): MedidaEditable => ({
  descripcion: f.description, nivel: (f.control_level as NivelControl) ?? '', responsableId: f.responsible_id ?? '',
  responsableNombre: f.responsible_name ?? '', fecha: f.due_date ?? '', seguimiento: f.follow_up ?? '',
});

// ── Validaciones (también las repite la base) ───────────────────────────────

export function validarArchivo(f: File | null | undefined): string | null {
  if (!f) return 'Elige un archivo.';
  if (!TIPOS_ARCHIVO.includes(f.type)) return 'El archivo debe ser PDF, PNG o JPEG.';
  if (f.size <= 0) return 'El archivo está vacío.';
  if (f.size > MAX_MB * 1024 * 1024) return `El archivo supera el tamaño máximo permitido (${MAX_MB} MB).`;
  return null;
}

export interface DatosAviso { numero: string; fechaEnvio: string; archivo: File | null }

export function validarAviso(d: DatosAviso, fechaCaso: string, hoy: string): string[] {
  const e: string[] = [];
  if (!d.numero.trim()) e.push('Escribe el número del aviso.');
  if (!esFechaIso(d.fechaEnvio)) e.push('Escribe la fecha de envío del aviso.');
  else {
    if (d.fechaEnvio < fechaCaso) e.push('La fecha de envío no puede ser anterior a la fecha del caso.');
    if (d.fechaEnvio > hoy) e.push('La fecha de envío no puede ser futura.');
  }
  const a = validarArchivo(d.archivo);
  if (a) e.push(a === 'Elige un archivo.' ? 'Sube la constancia del aviso (PDF o imagen).' : a);
  return e;
}

export function validarMedida(m: MedidaEditable): string[] {
  const e: string[] = [];
  if (!m.descripcion.trim()) e.push('Describe la medida.');
  if (!m.nivel) e.push('Elige el nivel en la jerarquía de controles.');
  if (!m.responsableId && !m.responsableNombre.trim()) e.push('Indica el responsable.');
  if (!esFechaIso(m.fecha)) e.push('Indica la fecha compromiso.');
  return e;
}

/** null si se puede cerrar: la medida ya tiene evidencia o se adjunta una ahora (válida). */
export function validarCierre(m: Pick<FilaMedida, 'evidence_path'>, archivo: File | null | undefined): string | null {
  if (archivo) return validarArchivo(archivo);
  return m.evidence_path?.trim() ? null : MENSAJE_CIERRE_SIN_EVIDENCIA;
}

export function validarReapertura(motivo: string): string | null {
  return motivo.trim().length >= MIN_MOTIVO_REAPERTURA ? null : 'Escribe el motivo para reabrir la medida.';
}

// ── Rutas ────────────────────────────────────────────────────────────────────

const marca = () => new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 14);
export const rutaConstancia = (companyId: string, incidentId: string, nombre: string) =>
  `${companyId}/${incidentId}/aviso/${marca()}-${nombreSeguroStorage(nombre)}`;
export const rutaEvidencia = (companyId: string, incidentId: string, medidaId: string, nombre: string) =>
  `${companyId}/${incidentId}/medidas/${medidaId}/${marca()}-${nombreSeguroStorage(nombre)}`;

export function nuevoId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  const b = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(b); else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const mensajeBase = (e: { code?: string; message: string }, que: string) =>
  e.code === '42501' ? `No tienes permiso para ${que}.` : `No se pudo ${que}: ${e.message}`;

async function subir(ruta: string, archivo: File): Promise<void> {
  const { error } = await supabase.storage.from(BUCKET_INCIDENTES).upload(ruta, archivo, { upsert: false, contentType: archivo.type });
  if (error) throw new Error(`No se pudo subir el archivo: ${error.message}`);
}
async function quitarArchivo(ruta: string | null | undefined): Promise<void> {
  if (!ruta) return;
  const { error } = await supabase.storage.from(BUCKET_INCIDENTES).remove([ruta]);
  if (error) console.error('No se pudo borrar el archivo del caso:', error);
}
const recalcular = (companyId: string) => syncIncidentCompliance(companyId).catch(err => { console.error('syncIncidentCompliance', err); return {}; });

// ── Aviso al IESS ──────────────────────────────────────────────────────────

export async function registrarAviso(companyId: string, incidentId: string, d: DatosAviso, previa: string | null): Promise<void> {
  if (!d.archivo) throw new Error('Sube la constancia del aviso (PDF o imagen).');
  const ruta = rutaConstancia(companyId, incidentId, d.archivo.name);
  await subir(ruta, d.archivo);
  const { error } = await supabase.from('incidents').update({
    iess_report_number: d.numero.trim(), iess_report_date: d.fechaEnvio, iess_constancia_path: ruta,
    iess_constancia_name: d.archivo.name.slice(0, 200), iess_constancia_mime: d.archivo.type, iess_constancia_size: d.archivo.size,
  }).eq('id', incidentId).eq('company_id', companyId);
  if (error) {
    await quitarArchivo(ruta);
    throw new Error(mensajeBase(error, 'registrar el aviso'));
  }
  if (previa && previa !== ruta) await quitarArchivo(previa);
  await recalcular(companyId);
}

export async function borrarAviso(companyId: string, incidentId: string, ruta: string | null): Promise<void> {
  const { error } = await supabase.from('incidents').update({ iess_constancia_path: null }).eq('id', incidentId).eq('company_id', companyId);
  if (error) throw new Error(mensajeBase(error, 'borrar el aviso'));
  await quitarArchivo(ruta);
  await recalcular(companyId);
}

// ── Medidas correctivas ───────────────────────────────────────────────────

export async function listarMedidas(incidentId: string): Promise<FilaMedida[]> {
  const { data, error } = await supabase.from('incident_actions').select(COLUMNAS_MEDIDA).eq('incident_id', incidentId).order('created_at');
  if (error) throw new Error(`No se pudieron cargar las medidas: ${error.message}`);
  return (data ?? []) as unknown as FilaMedida[];
}

const filaDesdeEditable = (m: MedidaEditable) => ({
  description: m.descripcion.trim(), control_level: m.nivel || null, responsible_id: m.responsableId || null,
  responsible_name: m.responsableNombre.trim() || null, due_date: m.fecha || null, follow_up: m.seguimiento.trim() || null,
});

export async function crearMedida(companyId: string, incidentId: string, m: MedidaEditable): Promise<string> {
  const errores = validarMedida(m);
  if (errores.length) throw new Error(errores.join(' '));
  const id = nuevoId();
  const { error } = await supabase.from('incident_actions').insert({ id, incident_id: incidentId, company_id: companyId, status: 'pendiente', ...filaDesdeEditable(m) });
  if (error) throw new Error(mensajeBase(error, 'guardar la medida'));
  await recalcular(companyId);
  return id;
}

export async function actualizarMedida(companyId: string, medida: FilaMedida, m: MedidaEditable, enSeguimiento: boolean): Promise<void> {
  const errores = validarMedida(m);
  if (errores.length) throw new Error(errores.join(' '));
  const cambio: Record<string, unknown> = filaDesdeEditable(m);
  if (medida.status !== ESTADO_CERRADA) cambio.status = enSeguimiento ? 'en_proceso' : 'pendiente';
  const { error } = await supabase.from('incident_actions').update(cambio).eq('id', medida.id).eq('company_id', companyId);
  if (error) throw new Error(mensajeBase(error, 'guardar la medida'));
  await recalcular(companyId);
}

/** Cierra la medida con la evidencia que ya tiene o con el archivo nuevo. Sin evidencia se rechaza (aquí y en la base). */
export async function cerrarMedida(companyId: string, medida: FilaMedida, archivo: File | null): Promise<void> {
  const motivo = validarCierre(medida, archivo);
  if (motivo) throw new Error(motivo);
  let ruta: string | null = null;
  const cambio: Record<string, unknown> = { status: ESTADO_CERRADA };
  if (archivo) {
    ruta = rutaEvidencia(companyId, medida.incident_id, medida.id, archivo.name);
    await subir(ruta, archivo);
    Object.assign(cambio, { evidence_path: ruta, evidence_name: archivo.name.slice(0, 200), evidence_mime: archivo.type, evidence_size: archivo.size });
  }
  const { error } = await supabase.from('incident_actions').update(cambio).eq('id', medida.id).eq('company_id', companyId);
  if (error) {
    await quitarArchivo(ruta);
    throw new Error(mensajeBase(error, 'cerrar la medida'));
  }
  if (ruta && medida.evidence_path && medida.evidence_path !== ruta) await quitarArchivo(medida.evidence_path);
  await recalcular(companyId);
}

export async function reabrirMedida(companyId: string, medida: FilaMedida, motivo: string): Promise<void> {
  const e = validarReapertura(motivo);
  if (e) throw new Error(e);
  const { error } = await supabase.from('incident_actions').update({ status: 'en_proceso', reopen_reason: motivo.trim() })
    .eq('id', medida.id).eq('company_id', companyId);
  if (error) throw new Error(mensajeBase(error, 'reabrir la medida'));
  await recalcular(companyId);
}

export async function borrarMedida(companyId: string, medida: FilaMedida): Promise<void> {
  if (medida.status === ESTADO_CERRADA) throw new Error(MENSAJE_BORRAR_CERRADA);
  // La base (M46) no deja borrar una medida cerrada: la RLS no da error, devuelve 0 filas.
  const { data, error } = await supabase.from('incident_actions').delete().eq('id', medida.id).eq('company_id', companyId).select('id');
  if (error) throw new Error(mensajeBase(error, 'borrar la medida'));
  if (!data || data.length === 0) throw new Error(MENSAJE_BORRAR_CERRADA);
  await quitarArchivo(medida.evidence_path);
  await recalcular(companyId);
}

// ── Imágenes para el PDF ───────────────────────────────────────────────────

/** Data URL de una imagen del bucket (URL firmada de 120 s); null si no es imagen o no se pudo leer. */
export async function imagenParaPdf(ruta: string | null | undefined, mime: string | null | undefined): Promise<string | null> {
  if (!ruta || !mime?.startsWith('image/')) return null;
  try {
    const { data, error } = await supabase.storage.from(BUCKET_INCIDENTES).createSignedUrl(ruta, SEGUNDOS_URL_FIRMADA);
    if (error || !data?.signedUrl) return null;
    const blob = await (await fetch(data.signedUrl)).blob();
    return await new Promise<string | null>(resolve => {
      const r = new FileReader();
      r.onload = () => resolve(typeof r.result === 'string' ? r.result : null);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
